import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { BlogPost } from '@/types';
import {
  ArrowLeft,
  Save,
  Send,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

interface FormState {
  title: string;
  slug: string;
  category: string;
  excerpt: string;
  body: string;
  status: 'draft' | 'published';
}

const emptyForm: FormState = {
  title: '',
  slug: '',
  category: '',
  excerpt: '',
  body: '',
  status: 'draft',
};

export default function BlogPostEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState<FormState>(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isNew && id) fetchPost(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPost = async (postId: string) => {
    setIsLoading(true);
    setLoadError('');
    try {
      const data = await api.getBlogPost(postId);
      const post: BlogPost = data.post;
      setForm({
        title: post.title || '',
        slug: post.slug || '',
        category: post.category || '',
        excerpt: post.excerpt || '',
        body: post.body || '',
        status: post.status || 'draft',
      });
      setSlugTouched(true);
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load blog post');
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (field: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-suggest slug from title on create until the slug is edited manually
      if (field === 'title' && isNew && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        body: form.body,
        category: form.category,
        status: form.status,
      };
      if (isNew) {
        const data = await api.createBlogPost(payload);
        navigate(`/blog/${data.post.id}`);
      } else {
        const data = await api.updateBlogPost(id!, payload);
        const post: BlogPost = data.post;
        setForm({
          title: post.title || '',
          slug: post.slug || '',
          category: post.category || '',
          excerpt: post.excerpt || '',
          body: post.body || '',
          status: post.status || 'draft',
        });
      }
    } catch (err: any) {
      // 409 slug conflict (or other server errors) — surface the server message
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to save blog post'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (isNew) return;
    setIsPublishing(true);
    setError('');
    try {
      const data = await api.publishBlogPost(id!);
      const post: BlogPost = data.post;
      setForm((prev) => ({ ...prev, status: post.status || 'published' }));
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to publish blog post'
      );
    } finally {
      setIsPublishing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="text-red-500" size={24} />
          <div>
            <h3 className="font-medium text-red-900">Error loading blog post</h3>
            <p className="text-red-700">{loadError}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/blog')}
          className="mt-4 text-red-700 hover:text-red-800 font-medium"
        >
          &larr; Back to blog
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to blog
        </button>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNew ? 'New Post' : 'Edit Post'}
            </h1>
            {!isNew && (
              <span
                className={`inline-flex items-center px-2 py-1 mt-2 text-xs font-medium rounded-full ${
                  form.status === 'published'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {form.status}
              </span>
            )}
          </div>

          <div className="flex gap-3 mt-4 lg:mt-0">
            {!isNew && form.status === 'draft' && (
              <button
                onClick={handlePublish}
                disabled={isPublishing || isSaving}
                className="px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50"
              >
                {isPublishing ? (
                  <Loader2 size={18} className="inline mr-2 animate-spin" />
                ) : (
                  <Send size={18} className="inline mr-2" />
                )}
                Publish
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving || isPublishing}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 size={18} className="inline mr-2 animate-spin" />
              ) : (
                <Save size={18} className="inline mr-2" />
              )}
              {isNew ? 'Create Post' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-red-500" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Post title"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slug
          </label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => {
              setSlugTouched(true);
              updateField('slug', e.target.value);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="post-url-slug"
          />
          <p className="mt-1 text-xs text-gray-500">
            {isNew
              ? 'Auto-suggested from the title. Leave blank to let the server derive it.'
              : 'The URL slug for this post.'}
          </p>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <input
            type="text"
            value={form.category}
            onChange={(e) => updateField('category', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g. Travel Health"
          />
        </div>

        {/* Excerpt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Excerpt
          </label>
          <textarea
            value={form.excerpt}
            onChange={(e) => updateField('excerpt', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="A short summary shown in listings..."
          />
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Body
          </label>
          <textarea
            value={form.body}
            onChange={(e) => updateField('body', e.target.value)}
            rows={18}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Write your post here..."
          />
          <p className="mt-1 text-xs text-gray-500">
            Accepts Markdown formatting.
          </p>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={form.status}
            onChange={(e) => updateField('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
      </div>
    </div>
  );
}
